'use client';

import type { CheckoutTireEfficiencyFlag } from '@packages/gql/generated/shopping/graphql';
import type { EfficiencyClass } from '../helper/isEfficiencyClass';
import { isEfficiencyClass } from '../helper/isEfficiencyClass';
import { DownloadItem } from '../../DownloadItem/DownloadItem.client';
import { tireOrderToUiTypeMap } from '../helper/mapping';
import { TireEfficiencyFlagItem } from '../atoms/TireEfficiencyFlagItem/TireEfficiencyFlagItem.client';
import { EnergyEfficiencyFlagItem } from '../atoms/EnergyEfficiencyFlagItem/EnergyEfficiencyFlagItem';

/**
 * Single power efficiency flag entry — typed as plain props so basket, wishlist
 * and AddToBasketLayer can map their slightly different GraphQL shapes
 * (`BasketPowerEfficiencyFlag`, `WishlistProductEnergyEfficiency`) into the same component.
 */
export type EfficiencyFlagsPowerFlag = {
  /**
   * EU energy class letter A-G. Items whose level is not a valid class
   * (e.g. legacy backend behaviour where tire data was smuggled into power flags
   * with composite levels like "B | 71 dB", or wishlist nullable shape) are silently skipped.
   */
  level: string | null;
  /** Localized label, used as overlay dialog title (e.g. "Energieeffizienzklasse"). */
  text: string | null;
  /** Image URL shown in the overlay dialog. */
  link: string;
  /** Optional product data sheet URL, rendered as an inline download link. */
  dataSheet?: string | null;
};

/**
 * Single tire efficiency flag entry — plain props matching the PascalCase enum
 * used by both `BasketProduct.tireEfficiencyFlags` (`CheckoutTireEfficiencyFlag`)
 * and `WishlistLineItem.tireEfficiency` (`WishlistTireEfficiencyLabel`).
 */
export type EfficiencyFlagsTireFlag = {
  type: CheckoutTireEfficiencyFlag['type'];
  /** Displayed text (e.g. "B" or "B | 71 dB" for rolling noise). */
  level: string;
  /** Image URL shown in the overlay dialog. */
  link: string;
};

export type EfficiencyFlagsProps = {
  powerFlags?: readonly EfficiencyFlagsPowerFlag[] | null;
  tireFlags?: readonly EfficiencyFlagsTireFlag[] | null;
  /**
   * Optional product data sheet URL rendered once below the tire flag row
   * (consistent with how the previous `<PowerEfficiency>` component rendered it for tires).
   */
  tireDataSheet?: string | null;
};

/**
 * Composite renderer for power and tire efficiency flags used across basket, wishlist
 * and AddToBasketLayer line items. Built on the shared UI primitives
 * `<EnergyEfficiencyFlag>` and `<TireEfficiencyFlag>` so all three line-item surfaces
 * use the same visuals as the rest of the shop (PDP, ProductCardV2).
 */
export const EfficiencyFlags = ({ powerFlags, tireFlags, tireDataSheet }: EfficiencyFlagsProps) => {
  const renderablePowerFlags = powerFlags?.filter((f) => isEfficiencyClass(f.level)) ?? [];
  const hasPower = renderablePowerFlags.length > 0;
  const hasTire = !!tireFlags && tireFlags.length > 0;

  if (!hasPower && !hasTire) return null;

  return (
    <div className="flex flex-col items-start gap-1">
      {hasPower && !hasTire && (
        <div className="flex flex-col items-start gap-1">
          {renderablePowerFlags.map((flag, idx) => (
            <div className="flex items-center gap-1" key={flag.level ?? idx}>
              <EnergyEfficiencyFlagItem
                hasOldLabel={false}
                level={flag.level as EfficiencyClass}
                linkToEnergieEfficiencyLabel={flag.link}
                overlayTitle={flag.text ?? undefined}
                dataSheetUrl={flag.dataSheet || undefined}
              />
            </div>
          ))}
        </div>
      )}
      {hasTire && (
        <div className="flex flex-row flex-wrap items-center gap-1">
          {tireFlags.map((flag) => (
            <TireEfficiencyFlagItem
              key={`${flag.type}-${flag.level}`}
              text={flag.level}
              type={tireOrderToUiTypeMap[flag.type]}
              linkToEnergieEfficiencyLabel={flag.link}
              size="normal"
            />
          ))}
        </div>
      )}
      {hasTire && tireDataSheet ? (
        <DownloadItem link={tireDataSheet} type="PRODUCT_DATASHEET" renderIcon={false} />
      ) : null}
    </div>
  );
};
